import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useBranding } from '../../context/BrandingContext';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BRANDING from '../../branding';

const SEO = ({ title, description, image, type = 'website', slug }) => {
    const { branding } = useBranding();
    const { i18n } = useTranslation();
    const location = useLocation();
    const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en';

    const b = branding || BRANDING;
    const currentPath = slug || location.pathname.split('/')[1] || 'home';

    // Look up custom page title/description from branding
    const pageBranding = b.seo?.pages?.[currentPath.toLowerCase()];

    const siteTitle = b.appName?.[lang] || (lang === 'ar' ? BRANDING.appName.ar : BRANDING.appName.en);
    const seoTitle = pageBranding?.title?.[lang] || b.seo?.defaultTitle?.[lang] || (lang === 'ar' ? BRANDING.seo.defaultTitle.ar : BRANDING.seo.defaultTitle.en);
    const seoDesc = pageBranding?.description?.[lang] || b.seo?.defaultDescription?.[lang] || (lang === 'ar' ? BRANDING.seo.defaultDescription.ar : BRANDING.seo.defaultDescription.en);

    // Page specific (prop) or branding-defined or site-wide fallback
    const finalTitle = title ? `${title} | ${siteTitle}` : (seoTitle === siteTitle ? seoTitle : `${seoTitle} - ${siteTitle}`);

    // Append selling points to description if they exist
    const sellingPoints = b.seo?.sellingPoints?.[lang] || (lang === 'ar' ? BRANDING.seo.sellingPoints.ar : BRANDING.seo.sellingPoints.en) || [];
    let finalDescription = description || seoDesc;
    if (Array.isArray(sellingPoints) && sellingPoints.length > 0) {
        finalDescription += ` ${sellingPoints.join(lang === 'ar' ? '، ' : ', ')}`;
    }

    const finalLogo = image || b.logo?.path || (process.env.PUBLIC_URL + '/logo.png');

    return (
        <Helmet>
            <title>{finalTitle}</title>
            <meta name="description" content={finalDescription} />

            {/* Icons */}
            {b.favicon?.path && <link rel="icon" href={b.favicon.path} />}
            {b.appleTouchIcon?.path && <link rel="apple-touch-icon" href={b.appleTouchIcon.path} />}

            {/* OpenGraph */}
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDescription} />
            <meta property="og:type" content={type} />
            <meta property="og:image" content={finalLogo} />
            <meta property="og:site_name" content={siteTitle} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={finalDescription} />
            <meta name="twitter:image" content={finalLogo} />

            {/* Application Colors */}
            {b.colors?.primary && <meta name="theme-color" content={b.colors.primary} />}

            {/* Custom Head Tags */}
            {b.customHeadTags && b.customHeadTags.split('\n').filter(Boolean).map((tag, index) => (
                <Helmet key={index}>
                    <div dangerouslySetInnerHTML={{ __html: tag.trim() }} />
                </Helmet>
            ))}
        </Helmet>
    );
};

export default SEO;
